import type { AnchorHTMLAttributes,ReactNode } from "react";
import { usePageContext } from "vike-react/usePageContext";

import { currentIcp, mergeIcp } from "./icp";

/**
 * Vike-compatible routing seam. Every layer above imports Link / navigate /
 * useRoute / useLocale from here. The hash router was replaced with
 * Vike's path-based router, locale is prepended to all links automatically.
 */

export function useRoute(): string {
  const pc = usePageContext();
  return pc.urlLogical ?? pc.urlPathname ?? "/";
}

export function useLocale(): string {
  const pc = usePageContext();
  return pc.locale ?? "it";
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  to: string;
  children: ReactNode;
};

/** Client-side navigation; resolves once the new page has rendered (Vike's own promise). */
export function navigate(to: string): Promise<void> {
  return import("vike/client/router").then((m) => {
    const locale =
      typeof document !== "undefined"
        ? document.documentElement.lang || "it"
        : "it";
    const prefixed = to.startsWith(`/${locale}`) ? to : `/${locale}${to}`;
    const withIcp = mergeIcp(prefixed, currentIcp(), window.location.origin);
    return m.navigate(withIcp);
  });
}

export function Link({ to, children, ...rest }: LinkProps) {
  const locale = useLocale();
  const href = to.startsWith(`/${locale}`) ? to : `/${locale}${to}`;
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  );
}
